import Vue from 'vue'
import VueI18n from 'vue-i18n'
import axios from 'axios'
import { Md5 } from 'md5-typescript'

Vue.use(VueI18n)

const SystemLanguage = 'en'

const formatMissing = (key: string, markMissing: boolean, language: string) => {
  const text = key.indexOf('c:') === 0 ? key.split(':').pop() : key
  return markMissing && language !== SystemLanguage ? '[' + text + ']' : text
}

/**
 * Handles talking to the i18n server,
 * fetching messages, reporting missing & usage.
 */
class I18nSrv {
  // Options
  private server: string
  private readonly reportMissing: boolean
  private readonly reportUsage: boolean

  // State
  private loadedLanguages: Array<string> = []
  private messagesMissing: Record<string, Array<string>> = {}
  private messagesUsage: Record<string, string> = {}
  private dataset: string
  private reporterRunning = false
  private reporterChecksum = ''
  private reportInterval = 5000
  private markMissing = false

  public constructor(
    server: string,
    reportMissing: boolean,
    reportUsage: boolean,
    reportInterval: number,
    dataset: string,
    markMissing: boolean,
  ) {
    this.server = server
    this.dataset = dataset
    this.reportMissing = reportMissing
    this.reportUsage = reportUsage
    this.reportInterval = reportInterval
    this.markMissing = markMissing
    this.scheduleReport()
  }

  // Called each time a translation is missing
  // Keep track of missing translations for reporting later, if configured
  public missingHandler(locale: string, key: string): void {
    if (!this.reportMissing) {
      return
    }
    if (locale === SystemLanguage) {
      // No need to report these as missing, because the original key text is the translated text
      return
    }
    if (!this.messagesMissing[locale]) {
      this.messagesMissing[locale] = []
    }
    if (this.messagesMissing[locale].indexOf(key) === -1) {
      this.messagesMissing[locale].push(key)
    }
  }

  // Called each time a translation is executed
  // Keep track of used translations for reporting later, if configured
  public postTranslationHandler(key: string): void {
    if (!this.reportUsage || !key) {
      return
    }
    this.messagesUsage[key] = I18nSrv.getNowDate()
  }

  private static getNowDate(): string {
    const date = new Date()
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    const monthStr = month < 10 ? '0' + month : '' + month
    const dayStr = day < 10 ? '0' + day : '' + day
    return year + '-' + monthStr + '-' + dayStr
  }

  // Load messages for a language from server
  public loadMessages(language: string): Promise<Record<string, string>> {
    return new Promise((resolve, reject) => {
      if (this.loadedLanguages.includes(language)) {
        resolve()
        return
      }
      axios
        .get(this.server + '/messages/' + this.dataset + '/' + language)
        .then((response) => {
          this.loadedLanguages.push(language)
          const r: Record<string, string> = {}
          for (let i = 0; i < response.data.length; i++) {
            const item = response.data[i]
            // If we have missing translations, do include them, so that we don't waste cycles reporting them again,
            // but be sure to mark them if the options says to do so.
            r[item.key] = item.status === 0 || item.translation === '' ? formatMissing(item.key, this.markMissing, language) : item.translation
          }
          resolve(r)
        })
        .catch((err) => {
          const errorMessage =
            'Failed loading translations: ' + (err?.response?.data?.error || '(No server error message)') + ' : ' + err
          reject(new Error(errorMessage))
        })
    })
  }

  private scheduleReport(): void {
    if (!this.reportInterval) {
      return
    }
    if (!this.reportMissing && !this.reportUsage) {
      return
    }
    setInterval(() => {
      this.report()
    }, this.reportInterval)
  }

  public report(): void {
    // Only run one instance at a time

    if (this.reporterRunning) {
      return
    }
    this.reporterRunning = true

    // Build payload

    const data = { dataset: this.dataset, missing: null, usage: null }
    if (this.reportMissing) {
      data.missing = []
      for (const language in this.messagesMissing) {
        data.missing.push({ language: language, keys: this.messagesMissing[language] })
      }
    }
    if (this.reportUsage) {
      data.usage = []
      for (const key in this.messagesUsage) {
        data.usage.push({ key: key, date: this.messagesUsage[key] })
      }
    }

    // Checksum - Only submit if changed

    const checksum = Md5.init(JSON.stringify(data))
    if (checksum !== this.reporterChecksum) {
      this.reporterChecksum = checksum
      axios
        .post(this.server + '/report', data)
        .then(() => {
          // successfully submitted to server
        })
        .catch((err) => {
          const errorMessage =
            'Failed submitting report: ' + (err?.response?.data?.error || '(No server error message)') + ' : ' + err
          console.error(errorMessage)
        })
    }

    this.reporterRunning = false
  }
}

/**
 * Adjust constructor options to VueI18n to add our custom parameters, notably server url.
 * And also use this to piggy-back the constructed I18nSrv from static context into VueI18n instance.
 */
type I18nSrvOptions = VueI18n.I18nOptions & {
  server: string
  dataset?: string
  markMissing?: boolean
  reportMissing?: boolean
  reportUsage?: boolean
  reportInterval?: number
  _i18nSrv?: I18nSrv
}

/**
 * Customized version of VueI18n.
 *
 * Important:
 *
 * 1. Use vueI18n.setLanguage(lang:string) instead of vueI18n.locale=lang
 *    or else messages will not be properly loaded from server.
 *
 * 2. You must provide "server" in constructor options
 *
 * 3. These options cannot be provided (because we override them):
 *    - messages
 *    - missing
 *    - postTranslation
 *    - silentTranslationWarn
 *
 * Apart from that, it should act basically like the standard VueI18n, so typically refer to their official docs.
 *
 * What's customized in our version:
 *
 * - Messages are stored on server, and fetched when language is changed (one chunk per language)
 * - Missing translations are kept track of, and periodically reported back to server
 * - Which translations are in use are kept track of, and periodically reported back to server
 *
 */
class VueI18nCustom extends VueI18n {
  private i18nSrv: I18nSrv

  public constructor(options: I18nSrvOptions) {
    super(VueI18nCustom.setDefaultOptions(options))
    this.i18nSrv = options._i18nSrv
  }

  private static setDefaultOptions(options: I18nSrvOptions): I18nSrvOptions {
    if (!options) {
      throw new Error('Must provide at least options.server to VueI18nSrv constructor')
    }

    // Defaults that can be overridden (see standard VueI18n docs for others)

    if (options.locale === undefined) {
      options.locale = SystemLanguage
    }
    if (options.fallbackLocale === undefined) {
      options.fallbackLocale = SystemLanguage
    }
    if (options.markMissing === undefined) {
      options.markMissing = true
    }
    if (options.reportMissing === undefined) {
      options.reportMissing = true
    }
    if (options.reportUsage === undefined) {
      options.reportUsage = true
    }
    if (options.reportInterval === undefined) {
      options.reportInterval = 5000
    }
    if (options.dataset === undefined) {
      options.dataset = 'default'
    }

    // Init i18nSrv

    options._i18nSrv = new I18nSrv(
      options.server,
      options.reportMissing,
      options.reportUsage,
      options.reportInterval,
      options.dataset,
      options.markMissing,
    )

    // Options that we will force for integration with i18nSrv

    options.silentTranslationWarn = true
    options.messages = {}
    options.messages[options.locale] = {}

    options.missing = (locale, key): string => {
      options._i18nSrv.missingHandler(locale, key)
      return formatMissing(key, options.markMissing, locale)
    }

    options.postTranslation = (text, key): string => {
      options._i18nSrv.postTranslationHandler(key)
      return text
    }

    return options
  }

  public setLanguage(language: string): void {
    if (language === this.locale) {
      return
    }
    this.i18nSrv
      .loadMessages(language)
      .then((messages) => {
        if (messages) {
          // Messages can be empty, if we've already loaded them before
          this.setLocaleMessage(language, messages)
        }
        document.querySelector('html').setAttribute('lang', language)
        this.locale = language
      })
      .catch((err) => {
        console.error('Error loading messages for language: ' + language + ' failed:', err)
      })
  }

  public report(): void {
    this.i18nSrv.report()
  }
}

export default VueI18nCustom
